import { formatDateInTimezone, getSessionCalendarDateString } from 'src/app/services/common/utils';

const MEMBERSHIP_CHANGE_FAILED = 'We could not confirm the membership change. Please check your membership status before trying again.';

/**
 * Calendar date already chosen for a membership change (Upgrade Now or Next Cycle).
 * A YYYY-MM-DD prefix is kept as-is so a date-only value is not reinterpreted in the browser timezone.
 * When nothing was chosen, today is the facility calendar date, not the visitor's.
 */
export function resolveMembershipStartCalendarDate(
  startAt: string | Date | null | undefined,
  facilityTimeZone: string,
  now: Date = new Date()
): string {
  if (typeof startAt === 'string') {
    const preserved = getSessionCalendarDateString(startAt);
    if (preserved) {
      return preserved;
    }
  }
  return formatDateInTimezone(now, facilityTimeZone);
}

/**
 * Upgrade Now uses the facility's current calendar date.
 * Next Cycle keeps the date-only NextChargableDate.
 */
export function upgradeTimeStartCalendarDate(options: {
  upgradeNow: boolean;
  nextChargableDate?: string | null;
  facilityTimeZone: string;
  now?: Date;
}): string {
  const now = options.now ?? new Date();
  if (options.upgradeNow) {
    return formatDateInTimezone(now, options.facilityTimeZone);
  }
  const nextCycle = getSessionCalendarDateString(options.nextChargableDate ?? '');
  return nextCycle || formatDateInTimezone(now, options.facilityTimeZone);
}

/**
 * UTC midnight of a calendar date.
 * HttpClient serializes Date with toISOString(), and TransitionV2 compares StartAt.Date
 * without converting that value back into the facility timezone. UTC midnight keeps
 * the chosen calendar day in that comparison from any browser offset.
 */
export function calendarDateToApiStartAt(calendarDate: string): Date {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(calendarDate);
  if (!match) {
    return new Date(NaN);
  }
  return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
}

export function isFacilityCalendarToday(
  calendarDate: string,
  facilityTimeZone: string,
  now: Date = new Date()
): boolean {
  return calendarDate === formatDateInTimezone(now, facilityTimeZone);
}

/**
 * TransitionV2 returns Ok(true). HttpService posts with wantResponseAnyHow, so a failed
 * response arrives on the success channel as response.error, which is not always a string.
 * Null means the change succeeded. Any other value is a failure message.
 */
export function membershipTransitionError(result: unknown): string | null {
  if (result === true) {
    return null;
  }
  if (typeof result === 'string') {
    const text = result.trim();
    return text || MEMBERSHIP_CHANGE_FAILED;
  }
  if (result && typeof result === 'object') {
    const body = result as { error?: unknown; details?: unknown; message?: unknown };
    const parts = [body.error, body.details, body.message]
      .filter((part): part is string => typeof part === 'string' && part.trim().length > 0)
      .map((part) => part.trim());
    if (parts.length > 0) {
      return parts.join(' ');
    }
  }
  return MEMBERSHIP_CHANGE_FAILED;
}
